import { Tooltip } from "antd";
import { useCallback, useMemo, useState } from "react";
import type React from "react";
import styled from "styled-components";

import PropertyControlLoading from "legacy/pages/Editor/PropertyPane/PropertyControlLoading";
import { colors } from "styles/colors";
import { styleAsClass } from "styles/styleAsClass";

import { PixelInput } from "./PixelInput";

const firstItemInRowStyle = styleAsClass`
    .ant-input-number {
        padding-left: 18px;
    }
  .ant-input-number, .ant-input-number-group-addon {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: 0;
  }
`;

const secondItemInRowStyle = styleAsClass`
    .ant-input-number {
        padding-left: 18px;
    }
  .ant-input-number {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: 0;
  }
`;

const inputGroupStyle = styleAsClass`
  display: grid;
  grid-template-columns: 100%;
  grid-auto-rows: min-content;
  row-gap: 6px;
  flex-grow: 1;
  .ant-input-number {
    font-size: 12px;
  }
  .ant-input-number-group-wrapper {
    width: 100%;
  }
`;

const inputRowStyle = styleAsClass`
  display: grid;
  grid-template-columns: 50% 1px 50%;
  &:hover .divider {
    background: ${colors.ACCENT_BLUE_500};
  }
  > div {
    position: relative;
  }
  input {
    padding: 0px 6px;
  }
`;

const svgWrapStyle = styleAsClass`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: ${colors.GREY_700};
  }
`;

const dividerStyle = styleAsClass`
  height: 100%;
  width: 1px;
  background: ${colors.GREY_100};
  transition: background 0.2s;
  &[data-focus="true"] {
    background: ${colors.ACCENT_BLUE_500};
  }
`;

const svgWrapStyleLessPadding = styleAsClass`
  left: 6px;
`;
type Key =
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "topLeft"
  | "topRight"
  | "bottomLeft"
  | "bottomRight";

const getIconColor = ({
  focusedKey,
  key,
  values,
  iconColor,
  type,
}: {
  focusedKey: string | null;
  key: Key;
  values: { [K in Key]?: number | null };
  iconColor: string;
  type?: "padding" | "margin" | "border" | "spacing";
}): string => {
  return focusedKey === key ||
    ((type === "padding" || type === "margin") && Boolean(values[key]))
    ? iconColor
    : colors.GREY_300;
};

const LoadingWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

export const AdvancedSizeInputs = ({
  LeftIconComponent,
  values,
  tooltipLabel,
  onValueChange,
  onEnter,
  onBlur,
  onFocus,
  mode = "side",
  readOnly,
  type,
  loadingStates,
}: {
  LeftIconComponent: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  values: { [K in Key]?: number | null };

  tooltipLabel: string;
  onValueChange: (value: number | null, key: Key) => void;
  onEnter: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
  mode: "side" | "corner";
  readOnly?: boolean;
  allowNegativeValues?: boolean;
  type?: "padding" | "margin" | "border" | "spacing";
  loadingStates?:
    | {
        left?: boolean;
        right?: boolean;
        top?: boolean;
        bottom?: boolean;
      }
    | {
        topLeft?: boolean;
        topRight?: boolean;
        bottomLeft?: boolean;
        bottomRight?: boolean;
      };
}) => {
  const { keys, labels }: { keys: Array<Key>; labels: Array<string> } =
    useMemo(() => {
      if (mode === "side") {
        return {
          keys: ["left", "top", "right", "bottom"],
          labels: ["Left", "Top", "Right", "Bottom"],
        };
      }
      return {
        keys: ["topLeft", "topRight", "bottomLeft", "bottomRight"],
        labels: ["Top left", "Top right", "Bottom left", "Bottom right"],
      };
    }, [mode]);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const handleFocus = (key: string) => {
    setFocusedKey(key);
    onFocus?.();
  };

  const handleBlur = useCallback(() => {
    setFocusedKey(null);
    onBlur?.();
  }, [onBlur]);

  const [firstKey, secondKey, thirdKey, fourthKey] = keys;

  return (
    <div className={inputGroupStyle} data-type={type}>
      <div className={inputRowStyle}>
        <div>
          <div className={svgWrapStyle}>
            <Tooltip title={`${labels[0]} ${tooltipLabel}`}>
              <LeftIconComponent
                style={{
                  color: getIconColor({
                    focusedKey,
                    key: keys[0],
                    values,
                    iconColor: colors.ACCENT_BLUE_500,
                    type,
                  }),
                }}
              />
            </Tooltip>
          </div>
          {loadingStates?.[firstKey as keyof typeof loadingStates] ? (
            <LoadingWrapper>
              <PropertyControlLoading />
            </LoadingWrapper>
          ) : (
            <PixelInput
              title={firstKey}
              value={values[firstKey]}
              onChange={(value) => onValueChange(value, firstKey)}
              onPressEnter={onEnter}
              className={firstItemInRowStyle}
              onFocus={() => handleFocus(firstKey)}
              onBlur={handleBlur}
              disabled={readOnly}
            />
          )}
        </div>
        <div
          className={`${dividerStyle} divider`}
          data-focus={focusedKey === firstKey || focusedKey === secondKey}
          data-type={type}
        />
        <div>
          <div className={[svgWrapStyle, svgWrapStyleLessPadding].join(" ")}>
            <Tooltip title={`${labels[1]} ${tooltipLabel}`}>
              <LeftIconComponent
                style={{
                  transform: "rotate(90deg)",
                  color: getIconColor({
                    focusedKey,
                    key: keys[1],
                    values,
                    iconColor: colors.ACCENT_BLUE_500,
                    type,
                  }),
                }}
              />
            </Tooltip>
          </div>
          {loadingStates?.[secondKey as keyof typeof loadingStates] ? (
            <LoadingWrapper>
              <PropertyControlLoading />
            </LoadingWrapper>
          ) : (
            <PixelInput
              title={secondKey}
              value={values[secondKey]}
              onChange={(value) => onValueChange(value, secondKey)}
              onPressEnter={onEnter}
              className={secondItemInRowStyle}
              onFocus={() => handleFocus(secondKey)}
              onBlur={handleBlur}
              disabled={readOnly}
            />
          )}
        </div>
      </div>
      <div className={inputRowStyle}>
        <div>
          <div className={svgWrapStyle}>
            <Tooltip title={`${labels[2]} ${tooltipLabel}`}>
              <LeftIconComponent
                style={{
                  transform:
                    mode === "corner" ? "rotate(270deg)" : "rotate(180deg)",
                  color: getIconColor({
                    focusedKey,
                    key: keys[2],
                    values,
                    iconColor: colors.ACCENT_BLUE_500,
                    type,
                  }),
                }}
              />
            </Tooltip>
          </div>
          {loadingStates?.[thirdKey as keyof typeof loadingStates] ? (
            <LoadingWrapper>
              <PropertyControlLoading />
            </LoadingWrapper>
          ) : (
            <PixelInput
              title={thirdKey}
              value={values[thirdKey]}
              onChange={(value) => onValueChange(value, thirdKey)}
              onPressEnter={onEnter}
              className={firstItemInRowStyle}
              onFocus={() => handleFocus(thirdKey)}
              controls={false}
              onBlur={handleBlur}
              disabled={readOnly}
            />
          )}
        </div>
        <div
          className={`${dividerStyle} divider`}
          data-focus={
            (focusedKey === thirdKey && Boolean(values[fourthKey])) ||
            focusedKey === fourthKey
          }
          data-type={type}
        />
        <div>
          <div className={[svgWrapStyle, svgWrapStyleLessPadding].join(" ")}>
            <Tooltip title={`${labels[3]} ${tooltipLabel}`}>
              <LeftIconComponent
                style={{
                  transform:
                    mode === "corner" ? "rotate(180deg)" : "rotate(270deg)",
                  color: getIconColor({
                    focusedKey,
                    key: keys[3],
                    values,
                    iconColor: colors.ACCENT_BLUE_500,
                    type,
                  }),
                }}
              />
            </Tooltip>
          </div>
          {loadingStates?.[fourthKey as keyof typeof loadingStates] ? (
            <LoadingWrapper>
              <PropertyControlLoading />
            </LoadingWrapper>
          ) : (
            <PixelInput
              title={fourthKey}
              value={values[fourthKey]}
              onChange={(value) => onValueChange(value, fourthKey)}
              onPressEnter={onEnter}
              className={secondItemInRowStyle}
              onFocus={() => handleFocus(fourthKey)}
              controls={false}
              onBlur={handleBlur}
              disabled={readOnly}
            />
          )}
        </div>
      </div>
    </div>
  );
};
